/**
 * virtourQuote.js — VirTour split math + payout-coherent display lines
 *
 * A VirTour quote mirrors VirTour Media's own agent order: the line `rate` is
 * THEIR retail price and Roto Flight is paid `splitPct` of it. The quote total
 * is therefore Roto Flight's payout, never the retail.
 *
 * `splitPct` is a number 0-100, or null when it hasn't been entered yet. A
 * missing split earns nothing — an unfilled line is visibly costing money
 * rather than silently inheriting a rate.
 *
 * Money convention matches calcEngine: round every line to cents, then sum the
 * rounded values, so a printed line payout and the printed total always agree.
 */

import { fmtC } from './utils';

function round2(n) {
  return Math.round(n * 100) / 100;
}

/** Split percent as a usable number. null / '' / garbage all read as 0. */
export function vtSplitPct(line) {
  if (line?.splitPct == null || line.splitPct === '') return 0;
  const pct = parseFloat(line.splitPct);
  return Number.isFinite(pct) ? pct : 0;
}

/** True when the line has no split entered yet (renders as a warning). */
export function vtSplitMissing(line) {
  return line?.splitPct == null || line.splitPct === '';
}

/** VirTour's retail for one line: rate x qty. */
export function vtLineRetail(line) {
  return round2((parseFloat(line?.qty) || 0) * (parseFloat(line?.rate) || 0));
}

/** Roto Flight's payout for one line: retail x split%. */
export function vtLinePayout(line) {
  return round2(vtLineRetail(line) * vtSplitPct(line) / 100);
}

/**
 * Retail, payout and blended split over a set of lines. Per-line round then
 * sum (calcEngine convention), so these are the numbers the line cards show.
 *
 * @returns {{ retailTotal: number, payoutTotal: number, blendedPct: number }}
 */
export function vtTotals(lines) {
  let retailTotal = 0;
  let payoutTotal = 0;
  for (const line of lines || []) {
    retailTotal = round2(retailTotal + vtLineRetail(line));
    payoutTotal = round2(payoutTotal + vtLinePayout(line));
  }
  return {
    retailTotal,
    payoutTotal,
    blendedPct: retailTotal > 0 ? round2((payoutTotal / retailTotal) * 100) : 0,
  };
}

/** How many priced lines still have no split entered (a $0 line costs nothing). */
export function vtMissingCount(lines) {
  return (lines || []).filter((l) => vtSplitMissing(l) && vtLineRetail(l) > 0).length;
}

/**
 * Map VirTour lines to display lines whose amount IS the payout, so any
 * renderer that multiplies qty x rate (preview, PDF) prints rows that sum to
 * the quote total. The retail it came from is spelled out in the detail line.
 *
 * The rate is divided rather than the quantity collapsed, so per-image
 * products keep their count on the document.
 */
export function buildVirtourDisplayLines(lines) {
  return (lines || []).map((line) => {
    const qty = parseFloat(line?.qty) || 0;
    const payout = vtLinePayout(line);
    const note = `Retail ${fmtC(vtLineRetail(line))} × ${vtSplitPct(line)}%`;
    return {
      ...line,
      rate: qty > 0 ? payout / qty : payout,
      amount: payout,
      vtNote: note,
      detail: line?.detail ? `${line.detail} · ${note}` : note,
    };
  });
}
